const DB_NAME = 'palomar-files'
const STORE = 'files'

function openFallbackDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function saveFallback(path: string, data: Blob | Uint8Array) {
  const db = await openFallbackDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(data, path)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}

async function readFallback(path: string): Promise<Uint8Array | null> {
  const db = await openFallbackDb()
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).get(path)
    request.onsuccess = async () => {
      db.close()
      const value = request.result
      if (!value) return resolve(null)
      if (value instanceof Blob) return resolve(new Uint8Array(await value.arrayBuffer()))
      resolve(value)
    }
    request.onerror = () => reject(request.error)
  })
}

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  return navigator.storage?.getDirectory ? navigator.storage.getDirectory() : null
}

async function opfsFile(path: string, create: boolean) {
  const root = await opfsRoot()
  if (!root) return null
  const parts = path.split('/').filter(Boolean)
  let dir = root
  for (const part of parts.slice(0, -1)) dir = await dir.getDirectoryHandle(part, { create })
  return dir.getFileHandle(parts.at(-1)!, { create })
}

export async function writeLocalFile(path: string, data: Blob | Uint8Array): Promise<string> {
  try {
    const handle = await opfsFile(path, true)
    if (handle) {
      const writable = await handle.createWritable()
      const contents = data instanceof Blob
        ? data
        : data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer
      await writable.write(contents)
      await writable.close()
      return `opfs://${path}`
    }
  } catch (error) {
    console.warn('OPFS no disponible; se usa IndexedDB.', error)
  }
  await saveFallback(path, data)
  return `idb://${path}`
}

export async function readLocalFile(path: string): Promise<Uint8Array | null> {
  try {
    const handle = await opfsFile(path, false)
    if (handle) return new Uint8Array(await (await handle.getFile()).arrayBuffer())
  } catch {
    // El archivo aún no existe.
  }
  return readFallback(path)
}

export function localPath(reference: string) {
  return reference.replace(/^(opfs|idb):\/\//, '')
}

export async function readStoredFile(reference: string): Promise<Uint8Array | null> {
  return readLocalFile(localPath(reference))
}
